
import _ from 'lodash';
import axios, { CancelTokenSource } from 'axios';
import { debounce } from 'lodash';
import { Vue, Component, Prop } from 'vue-property-decorator';
import { QueryFilter } from '@nestjsx/crud-request/lib/types/request-query.types';
import {
  SConditionAND,
  SFields,
  RequestQueryBuilder,
  SFieldOperator,
  CreateQueryParams,
  CondOperator,
} from '@dataui/crud-request';
import Multiselect from 'vue-multiselect';
import { plainToClass } from 'class-transformer';
import { ClassType } from 'class-transformer/ClassTransformer';
import { DataUiBlueprints, SimplifiedColumnTypeEnum } from '@gid/models';

export interface SearchFieldDescription {
  field: string;
  op: keyof SFieldOperator;
  init?: boolean;
}

interface Condition {
  [key: string]: any;
}

@Component({
  components: {
    Multiselect,
  },
})
export default class CrudMultiSelect extends Vue {
  @Prop({ required: true }) endpoint!: string;
  @Prop({ required: true }) searchFields!: (string | SearchFieldDescription)[];
  @Prop({ required: true }) trackBy!: string;
  @Prop({ required: false }) filters?: Array<QueryFilter>;
  @Prop({ required: true }) label!: string;
  @Prop() customLabel!: (i: any) => string;
  @Prop({ default: false }) multiple!: boolean;
  @Prop({ default: false }) selectOnly!: boolean;
  @Prop({ default: false }) allowEmptySearch!: boolean;
  @Prop({ default: Infinity }) maxResults!: number;
  @Prop() placeholder!: string;
  @Prop({ default: null }) value!: any;
  @Prop({ default: null }) transformValue!: (any) => any;
  @Prop({ default: true }) partial!: boolean;
  @Prop({ default: false }) initFetchLabels!: boolean;
  @Prop() model!: ClassType<unknown>;
  @Prop() queryParams!: CreateQueryParams;
  @Prop({ default: false }) disabled!: boolean;
  @Prop({ default: 50 }) resultLimit!: number;
  @Prop() nameProperties!: any;

  isLoading: boolean = false;
  options: Array<any> = [];
  cancelSource: CancelTokenSource | null = null;

  created() {
    if (this.initFetchLabels) {
      let valuesArray;
      if (Array.isArray(this.value)) {
        valuesArray = this.value;
      } else if (this.value) {
        valuesArray = [this.value];
      } else {
        return;
      }
      const search = this.buildFetchLables(
        valuesArray.map((v) => v[this.trackBy]),
      );
      if (search) {
        const qb = this.getQueryBuilder(this.queryParams);
        qb.search(search);
        const queryString = qb.query();

        this.fetch(queryString).then((data) => {
          const byValue = data.reduce(
            (r, v) => ({ ...r, [v[this.trackBy]]: v }),
            {},
          );
          for (const v of valuesArray) {
            this.$set(v, this.label, byValue[v[this.trackBy]][this.label]);
          }
        });
      }
    }
  }

  get _customLabel() {
    if (this.customLabel) {
      if (typeof this.customLabel === 'function') {
        return (entity) => {
          return this.customLabel(entity);
        };
      }
      return this.customLabel;
    }
    if (this.label.indexOf('.') > 0) {
      return (item) => _.get(item, this.label);
    }
    return undefined;
  }

  get selected() {
    return this.selectOnly ? undefined : this.value;
  }

  set selected(data) {
    this.$emit('input', data);
  }

  get showClear() {
    return (
      !this.selectOnly &&
      (Array.isArray(this.selected) ? this.selected.length : this.selected)
    );
  }

  clearAll() {
    this.selected = this.multiple ? [] : null;
  }

  executeQueryByType(
    entity: string | Function,
    query: string,
    prefix?: string,
  ) {
    const blueprint = DataUiBlueprints.get(entity);
    const searchWords = query.split(/\s+/);
    if (!blueprint || blueprint.searchFields?.length) {
      throw new Error('Field has no default search configuration');
    }
    return blueprint.searchFields?.default.map((f) => {
      const fieldName = prefix ? `${prefix}.${f}` : f;
      const column = blueprint.groups.table?.[f];
      if (column?.isRelation) {
        return {
          $or: this.executeQueryByType(column.relationEntity, query, fieldName),
        };
      }
      switch (column?.columnType.type) {
        case SimplifiedColumnTypeEnum.UUID:
        case SimplifiedColumnTypeEnum.TEXT:
          return searchWords.length > 1
            ? {
                $and: searchWords.map((word) => ({
                  [fieldName]: {
                    [CondOperator.CONTAINS_LOW]: word,
                  },
                })),
              }
            : {
                [fieldName]: {
                  [CondOperator.CONTAINS_LOW]: query,
                },
              };
        case SimplifiedColumnTypeEnum.INTEGER:
        case SimplifiedColumnTypeEnum.FLOAT:
          return {
            [fieldName]: {
              [CondOperator.EQUALS]: Number(query) || 0,
            },
          };

        default:
          return {
            [fieldName]: {
              [CondOperator.EQUALS]: query,
            },
          };
      }
    });
  }

  buildSearch(query) {
    const search: Array<SFields | SConditionAND> = [];
    const searchFields = this.searchFields?.filter(
      (f) => typeof f === 'string' || !f.init,
    );
    if (query && searchFields?.length) {
      if (this.model) {
        search.push({
          $or: this.executeQueryByType(this.model.name, query),
        });
      } else {
        search.push({
          $or: searchFields.map((fieldDesc) =>
            typeof fieldDesc === 'string'
              ? {
                  [fieldDesc]: {
                    $contL: query,
                  },
                }
              : {
                  [fieldDesc.field]: {
                    [fieldDesc.op]: query,
                  },
                },
          ),
        });
      }
    }

    if (this.queryParams?.search) {
      if (Array.isArray(this.queryParams?.search)) {
        search.push(...this.queryParams?.search);
      } else {
        search.push(this.queryParams?.search);
      }
    }

    return {
      $and: search,
    };
  }

  buildFetchLables(values) {
    if (this.searchFields?.length) {
      let fieldName =
        this.searchFields.find((fd) =>
          typeof fd !== 'string' ? fd.init : false,
        ) ?? this.searchFields[0];
      if (typeof fieldName !== 'string') {
        fieldName = fieldName.field;
      }
      return {
        $or: values.map((v) => ({
          [fieldName.toString()]: {
            $eq: v,
          },
        })),
      };
    }
  }

  async find(query) {
    if (!this.allowEmptySearch && !query) {
      this.options = [];
      return;
    }
    this.isLoading = true;
    const qb = this.getQueryBuilder(this.queryParams);
    const search = this.buildSearch(query);
    if (this.filters) {
      search.$and.push(...this.convertFiltersToSearchQuery(this.filters)); // include filters defined on models
    }
    qb.search(search);
    qb.setLimit(this.resultLimit);
    if (this.maxResults < Infinity) {
      qb.setLimit(this.maxResults);
    }
    const queryString = qb.query();
    this.fetch(queryString).then((data) => {
      this.options = data;
    });
  }
  // helper function to remap filters to filter object with values
  convertFiltersToSearchQuery(filters: QueryFilter[]): any {
    const andConditions: Condition = [];
    let orConditions: Condition = [];

    filters.forEach((filter) => {
      const { field, operator, value } = filter;

      if (operator === 'or') {
        orConditions.push({ [field]: value });
      } else {
        andConditions.push({ [field]: { [operator]: value } });
      }
    });

    if (orConditions.length > 0) {
      andConditions.push({ $or: orConditions });
    }

    return andConditions;
  }

  getQueryBuilder(params: CreateQueryParams) {
    const qb = RequestQueryBuilder.create(params);
    if (this.partial) {
      const labelSet = new Set([
        this.trackBy,
        this.label,
        ...this.searchFields.map((s) => (typeof s === 'string' ? s : s.field)),
      ]);
      if (this.nameProperties?.length) {
        this.nameProperties?.forEach((prop) => labelSet.add(prop));
      }
      qb.select([...labelSet]);
    }
    return qb;
  }

  async fetch(queryString) {
    if (this.cancelSource) {
      this.cancelSource.cancel();
    }
    this.isLoading = true;

    this.cancelSource = axios.CancelToken.source();
    return axios
      .get(`${this.endpoint}?${queryString}`, {
        cancelToken: this.cancelSource.token,
      })
      .then((result) => {
        let data = result.data.data ?? result.data;
        if (typeof this.transformValue === 'function') {
          data = data.map(this.transformValue);
        }
        if (this.model) {
          data = plainToClass(this.model, data);
        }
        return data;
      })
      .finally(() => {
        this.isLoading = false;
        this.cancelSource = null;
      });
  }

  throttledFind = debounce(this.find, 400);
}
